/**
 * Account item at the foot of the floating rail: the avatar, with everything
 * the always-collapsed rail has no room for in a menu that opens on hover —
 * the name, Conta / Equipe on my.x9.app, sign-out, and the HWYAH attribution.
 *
 * Conta and Equipe are billing, not work destinations, so they live here
 * rather than in the nav list. Click and keyboard still open it, so hover is
 * a shortcut, not the only way in.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logOutOutline, peopleOutline, personCircleOutline } from 'ionicons/icons';
import IonIcon from '../../components/IonIcon';
import UserChip from '../../components/UserChip';
import HwyahProductFooter from '../../components/HwyahProductFooter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';

/** Long enough to cross the gap between the avatar and the menu. */
const HOVER_CLOSE_MS = 200;
/** A hover-opened menu does not always move focus, so hover paints the row too. */
const itemClass = 'cursor-pointer hover:bg-accent hover:text-accent-foreground';

interface Props {
  displayName: string | null;
  photoURL: string | null;
  onSignOut?: () => void;
  /** Conta / Equipe rows — my.x9.app only, and not when the config rail already lists them. */
  billing?: boolean;
  className?: string;
}

export default function UserAccountMenu({
  displayName,
  photoURL,
  onSignOut,
  billing = false,
  className,
}: Props) {
  const { t } = useTranslation(['web', 'settings']);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  const show = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hideSoon = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  };
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onPointerEnter={show}
          onPointerLeave={hideSoon}
          onPointerDown={(event) => {
            // Hover already opened it; Radix's own toggle would close it again.
            event.preventDefault();
            show();
          }}
          className={cn(
            'flex size-8 items-center justify-center rounded-full outline-none ring-sidebar-ring focus-visible:ring-2',
            className,
          )}
          aria-label={displayName || t('web:nav.accountMenu')}
        >
          <UserChip displayName={displayName} photoURL={photoURL} className="gap-0 [&>*:first-child]:size-8" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="end"
        sideOffset={14}
        className="min-w-56"
        onPointerEnter={show}
        onPointerLeave={hideSoon}
      >
        {displayName ? (
          <DropdownMenuLabel className="truncate">{displayName}</DropdownMenuLabel>
        ) : null}
        {billing ? (
          <>
            <DropdownMenuItem asChild className={itemClass}>
              <a href="/account">
                <IonIcon icon={personCircleOutline} size={16} />
                {t('web:nav.account')}
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className={itemClass}>
              <a href="/seats">
                <IonIcon icon={peopleOutline} size={16} />
                {t('web:nav.seats')}
              </a>
            </DropdownMenuItem>
          </>
        ) : null}
        {onSignOut ? (
          <>
            {billing ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem onSelect={onSignOut} className={itemClass}>
              <IonIcon icon={logOutOutline} size={16} />
              {t('settings:signOut.label')}
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <HwyahProductFooter align="start" className="w-full rounded-sm px-2 py-1.5 hover:bg-accent" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
